import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input, Label } from '..';
import Dropdown from '../Dropdown';
import { INDUSTRIES, PLACEHOLDERS } from '../../../constants/onBoardingOrganization';

const addressFields = [
  { name: 'city', label: 'City' },
  {
    name: 'state',
    label: 'State',
    pattern: { value: /^[A-Za-z]{2}$/, message: 'Use 2-letter code' },
  },
  {
    name: 'zip_code',
    label: 'Zip Code',
    pattern: { value: /^\d{5}(-\d{4})?$/, message: 'Invalid ZIP' },
  },
];

/**
 * OrganizationFields component renders input fields used to collect
 * basic organization information: name, industry, street, city, state, and ZIP code.
 *
 * It uses react-hook-form for form control and validation, and integrates
 * custom UI components for styling and layout consistency.
 */
const OrganizationFields = () => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      {/* Organization Name and Industry dropdown */}
      <div className="grid grid-cols-2 gap-4">
        {/* Organization Name */}
        <div className="form-group">
          <Label variant="gray">Organization Name</Label>
          <Input
            variant="primary"
            customClass="!max-w-full"
            placeholder="Acme Inc."
            {...register('name', { required: true, maxLength: 100 })}
          />
          {errors.name && <p className="error">Required</p>}
        </div>

        {/* Industry dropdown with controlled input */}
        <div className="form-group">
          <Label variant="gray">Industry</Label>
          <Controller
            name="industry"
            control={control}
            rules={{ required: true }}
            render={({ field }) => {
              const selectedLabel = INDUSTRIES.find((o) => o.value === field.value)?.label;
              return (
                <Dropdown
                  options={INDUSTRIES}
                  text="Select industry…"
                  widthClass="w-full"
                  selectedOption={selectedLabel}
                  onSelect={(opt) => field.onChange(opt.value)}
                  onDropdownToggle={() => {}}
                />
              );
            }}
          />
          {errors.industry && <p className="error">Required</p>}
        </div>
      </div>

      {/* Street input */}
      <div className="form-group !mt-[8px]">
        <Label variant="gray">Street</Label>
        <Input
          variant="primary"
          customClass="!max-w-full"
          placeholder="123 Main St"
          {...register('street', { required: true })}
        />
        {errors.street && <p className="error">Required</p>}
      </div>

      {/* City, State, and ZIP Code fields */}
      <div className="grid grid-cols-3 gap-4">
        {addressFields.map(({ name, label, pattern }) => (
          <div className="form-group" key={name}>
            <Label variant="gray">{label}</Label>
            <Input
              variant="primary"
              customClass="!max-w-full"
              placeholder={PLACEHOLDERS[name]}
              {...register(name, {
                required: 'Required',
                ...(pattern && { pattern }),
              })}
            />
            {errors[name] && <p className="error">{errors[name].message}</p>}
          </div>
        ))}
      </div>
    </>
  );
};

export default OrganizationFields;
